import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Two gold confetti "cannons" that fire upward-inward from the ends of the #1
 * row whenever the row is hovered — each emits a short stream (not a single
 * pop), so it reads like a pair of confetti guns shooting up into the air. A
 * full-viewport 2D canvas is portaled to <body> so particles can fly anywhere;
 * it's pointer-events:none and only animates while particles/emitters are live.
 *
 * Honors prefers-reduced-motion (no fire). No third-party dependency.
 */

const COLORS = ['#ffc20e', '#fed246', '#ffdf86', '#d8ae11', '#fff7de', '#ffffff'];
const STREAM_MS = 450; // how long each cannon fires
const EMIT_RATE = 130; // particles per second, per cannon
const GUN_SPREAD = Math.PI * 0.2; // jet cone width (radians)
const GUN_TILT = 0.32; // radians each cannon tilts inward from straight up
const SPEED_MIN = 11;
const SPEED_VAR = 6;
const COOLDOWN = 650; // ms — >= STREAM_MS so re-hovers don't stack streams

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  spin: number;
  size: number;
  color: string;
  life: number;
}

interface Emitter {
  x: number;
  y: number;
  aim: number; // launch angle (radians)
  remaining: number; // ms left to fire
  acc: number; // fractional-particle accumulator
}

export function RowConfetti({
  rowRef,
  refreshKey,
}: {
  rowRef: React.RefObject<HTMLTableRowElement | null>;
  refreshKey?: string | number;
}): React.ReactPortal {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const row = rowRef.current;
    const canvas = canvasRef.current;
    if (!row || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles: Particle[] = [];
    let emitters: Emitter[] = [];
    let raf = 0;
    let lastTime = 0;
    let lastFire = 0;

    const resize = (): void => {
      canvas.width = Math.round(window.innerWidth * dpr);
      canvas.height = Math.round(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const spawn = (e: Emitter): void => {
      const angle = e.aim + (Math.random() - 0.5) * GUN_SPREAD;
      const speed = SPEED_MIN + Math.random() * SPEED_VAR;
      particles.push({
        x: e.x,
        y: e.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rot: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.4,
        size: 5 + Math.random() * 5,
        color: COLORS[(Math.random() * COLORS.length) | 0] ?? COLORS[0]!,
        life: 1,
      });
    };

    const step = (now: number): void => {
      const dtMs = lastTime ? Math.min(now - lastTime, 50) : 16.667;
      lastTime = now;
      const dt = dtMs / 16.667; // ~60fps frames elapsed
      const drag = Math.pow(0.98, dt);

      // Emit from the active cannons for their firing window.
      for (const e of emitters) {
        e.remaining -= dtMs;
        e.acc += EMIT_RATE * (dtMs / 1000);
        let n = Math.floor(e.acc);
        e.acc -= n;
        while (n-- > 0) spawn(e);
      }
      emitters = emitters.filter((e) => e.remaining > 0);

      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (const p of particles) {
        p.vx *= drag;
        p.vy = p.vy * drag + 0.25 * dt; // gravity
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.spin * dt;
        p.life -= 0.012 * dt;
        if (p.life <= 0) continue;
        const h = p.size * 0.6;
        ctx.save();
        ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -h / 2, p.size, h);
        ctx.restore();
      }
      particles = particles.filter((p) => p.life > 0 && p.y < window.innerHeight + 40);

      if (particles.length > 0 || emitters.length > 0) {
        raf = requestAnimationFrame(step);
      } else {
        raf = 0;
        lastTime = 0;
        ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      }
    };

    const fire = (): void => {
      // A cannon at each end of the row, angled up and inward.
      const r = row.getBoundingClientRect();
      emitters.push(
        { x: r.left + 10, y: r.bottom, aim: -Math.PI / 2 + GUN_TILT, remaining: STREAM_MS, acc: 0 },
        { x: r.right - 10, y: r.bottom, aim: -Math.PI / 2 - GUN_TILT, remaining: STREAM_MS, acc: 0 },
      );
      if (!raf) {
        lastTime = 0;
        raf = requestAnimationFrame(step);
      }
    };

    const onEnter = (): void => {
      if (prefersReduced) return;
      const now = performance.now();
      if (now - lastFire < COOLDOWN) return;
      lastFire = now;
      fire();
    };

    row.addEventListener('mouseenter', onEnter);
    window.addEventListener('resize', resize);
    return () => {
      row.removeEventListener('mouseenter', onEnter);
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(raf);
    };
  }, [rowRef, refreshKey]);

  return createPortal(
    <canvas ref={canvasRef} className="row-confetti" aria-hidden="true" />,
    document.body,
  );
}
